export type DceSourceType = 'ZIP' | 'LOOSE_FILES';

export type DceUploadStatus =
  | 'PENDING'
  | 'UNZIPPING'
  | 'CLASSIFYING'
  | 'EXTRACTING'
  | 'INDEXING'
  | 'DONE'
  | 'PARTIAL_SUCCESS'
  | 'FAILED';

export interface DceUpload {
  id: number;
  opportunityId: number;
  uploadedById: number;
  uploadedAt: string;
  sourceType: DceSourceType;
  sourceFilename: string | null;
  /** BigInt sérialisé en string par l'API (JSON safe). */
  sourceSizeBytes: string | null;
  fileCount: number;
  failedCount: number;
  totalSizeBytes: string;
  status: DceUploadStatus;
  errorMessage: string | null;
  version: number;
  parentUploadId: number | null;
  isCurrent: boolean;
  classificationApprovedAt: string | null;
  deletedAt: string | null;
}

export interface DcePresignFileRequest {
  filename: string;
  sizeBytes: number;
  contentType?: string;
}

export interface CreatePresignedUploadRequest {
  sourceType: DceSourceType;
  files: DcePresignFileRequest[];
  parentUploadId?: number;
}

export interface DcePresignedFileResponse {
  path: string;
  url: string;
  contentType?: string;
  filename: string;
}

export interface CreatePresignedUploadResponse {
  uploadId: number;
  files: DcePresignedFileResponse[];
}

export const DCE_UPLOAD_STATUS_LABELS: Record<DceUploadStatus, string> = {
  PENDING: 'En attente',
  UNZIPPING: 'Décompression',
  CLASSIFYING: 'Classification',
  EXTRACTING: 'Extraction',
  INDEXING: 'Indexation',
  DONE: 'Terminé',
  PARTIAL_SUCCESS: 'Terminé partiellement',
  FAILED: 'Échec',
};

export const TERMINAL_STATUSES: DceUploadStatus[] = [
  'DONE',
  'PARTIAL_SUCCESS',
  'FAILED',
];

export function isTerminalStatus(status: DceUploadStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export type DceDocumentType =
  | 'RC'
  | 'CCAP'
  | 'CCTP'
  | 'AE'
  | 'BPU'
  | 'DPGF'
  | 'DQE'
  | 'DC1'
  | 'DC2'
  | 'DC4'
  | 'DUME'
  | 'MEMOIRE_TYPE'
  | 'ANNEX'
  | 'PLAN'
  | 'OTHER';

export type DceClassifiedBy = 'RULE' | 'AI' | 'MANUAL';

export const DCE_DOCUMENT_TYPE_LABELS: Record<DceDocumentType, string> = {
  RC: 'Règlement de consultation',
  CCAP: 'CCAP',
  CCTP: 'CCTP',
  AE: 'Acte d\'engagement',
  BPU: 'BPU',
  DPGF: 'DPGF',
  DQE: 'DQE',
  DC1: 'DC1',
  DC2: 'DC2',
  DC4: 'DC4',
  DUME: 'DUME',
  MEMOIRE_TYPE: 'Mémoire type / Cadre de réponse',
  ANNEX: 'Annexe',
  PLAN: 'Plan',
  OTHER: 'Autre',
};

export const DCE_DOCUMENT_TYPES: DceDocumentType[] = [
  'RC',
  'CCAP',
  'CCTP',
  'AE',
  'BPU',
  'DPGF',
  'DQE',
  'DC1',
  'DC2',
  'DC4',
  'DUME',
  'MEMOIRE_TYPE',
  'ANNEX',
  'PLAN',
  'OTHER',
];

export interface DceSearchRequest {
  query: string;
  topK?: number;
  documentTypes?: DceDocumentType[];
}

export interface DceSearchResultChunk {
  chunkId: number;
  documentId: number;
  documentFilename: string;
  documentType: DceDocumentType | null;
  lotId: number | null;
  pageNumber: number | null;
  sectionTitle: string | null;
  content: string;
  similarity: number;
}

export interface DceUploadDocument {
  id: number;
  filename: string;
  filePath: string;
  fileSize: number | null;
  mimeType: string | null;
  dceRelativePath: string | null;
  sha256: string | null;
  dceDocumentType: DceDocumentType | null;
  dceClassifiedBy: DceClassifiedBy | null;
  dceClassificationConf: number | null;
  dceClassifiedAt: string | null;
  lotId: number | null;
  createdAt: string;
}
